import { useEffect, useMemo, useState } from 'react';
import { activePlayer, chooseAction, createGame, getLegalActions, getPlayerView, Rng, tryApplyAction, type PlayerView } from '@estandarte/engine';
import { useGame } from '../store/game';
import { resumableSession, useOnline } from '../store/online';
import { MapView } from '../game/MapView';
import { GameCard } from '../game/Cards';
import { CharacterSheet } from '../game/PlayerBio';
import { art, crestOf, sigilOf } from '../art';
import { BOT_NAMES, CHARACTERS, CHRONICLES, S, theme } from '../theme';
import { Icon, type IconName } from '../ui/Icon';
import { LOCAL_ONLY } from '../lib/env';


/** Uma partida de demonstração (só bots, seed fixa) para o mapa da capa ter bandeiras e tropas. */
function demoView(): PlayerView | null {
  try {
    const colors = theme.guilds.map((g) => g.color).slice(0, 4);
    let s = createGame({
      seed: 20250,
      content: theme.content,
      seats: colors.map((color, i) => ({ name: BOT_NAMES[i], color, vocation: (['defense', 'range', 'damage', 'heal'] as const)[i], kind: 'bot' })),
    });
    const rng = new Rng(7);
    for (let i = 0; i < 4000 && s.phase !== 'over' && s.round < 3; i++) {
      const p = activePlayer(s);
      if (p === null) break;
      const res = tryApplyAction(s, chooseAction(getPlayerView(s, p), getLegalActions(s, p), rng));
      if (!res.ok) break;
      s = res.state;
    }
    return getPlayerView(s, null);
  } catch {
    return null;
  }
}

const FACTS: { n: string; label: string }[] = [
  { n: '3–6', label: 'guildas na mesa' },
  { n: '30', label: 'territórios em 6 regiões' },
  { n: '15', label: 'missões secretas' },
  { n: '6', label: 'bosses com raids' },
  { n: '15', label: 'cartas de loot' },
  { n: '~7', label: 'rodadas por partida' },
];

const STEPS: { icon: IconName; title: string; text: string }[] = [
  {
    icon: 'flag',
    title: 'Reforce',
    text: 'Receba tropas pelos seus territórios, pelas regiões inteiras que domina e pelas trocas de cartas. Coloque onde a fronteira aperta.',
  },
  {
    icon: 'sword',
    title: 'Ataque',
    text: 'Role até 3 dados contra os do defensor; o maior contra o maior, empate é da defesa. Conquistou? Leve tropas para dentro e ganhe uma carta no fim do turno.',
  },
  {
    icon: 'skull',
    title: 'Cace bosses',
    text: 'Bosses surgem nos covis. Cada 2 de vida que você tira vale uma carta; o último golpe leva o loot. Se ninguém der conta, vem a raid.',
  },
  {
    icon: 'crown',
    title: 'Cumpra a missão',
    text: 'Sua missão é secreta: dominar regiões, portos, territórios ou derrubar uma guilda. Cumpriu, venceu. Senão, no fim da rodada 10 vem o Server Save.',
  },
];

const SAMPLE_LOOT = ['strike', 'barrage', 'luck', 'captain', 'blessing'] as const;

export function Home() {
  const { go, hasSave, loadSave, error } = useGame();
  const online = useOnline();
  const [saved] = useState(hasSave);
  const [session, setSession] = useState(() => (LOCAL_ONLY ? null : resumableSession()));
  const demo = useMemo(demoView, []);
  const [rival, setRival] = useState<string>(BOT_NAMES[0]);
  // Outra aba pode liberar a sessão a qualquer momento.
  useEffect(() => {
    if (LOCAL_ONLY) return;
    const t = setInterval(() => setSession(resumableSession()), 2000);
    return () => clearInterval(t);
  }, []);
  const rivalColor = theme.guilds[BOT_NAMES.indexOf(rival) % theme.guilds.length].color;
  return (
    <div className="page home">
      <div className="hero">
        <div>
          <div className="logo-lockup">
            <img src={art.logoMark} alt="" />
            <div>
              <h1>{S.appTitle}</h1>
              <div className="sub">{S.appSubtitle}</div>
            </div>
          </div>
          <p className="lede">
            Um jogo de estratégia por turnos no mundo de Tibia. Sua guilda disputa o continente com até cinco rivais: conquiste territórios,
            cace bosses pelo último golpe e cumpra sua missão secreta antes do Server Save.
          </p>
          <div className="menu">
            {saved && (
              <button className="btn primary" onClick={loadSave}>
                Continuar partida local <small>salva neste navegador</small>
              </button>
            )}
            {session && (
              <button className="btn primary" onClick={() => online.resume(session.token)}>
                Voltar à sala {session.code} <small>como {session.name || 'você'}</small>
              </button>
            )}
            <button className={`btn${saved || session ? '' : ' primary'}`} onClick={() => go('localSetup')}>
              Jogar local <small>contra bots ou hot-seat</small>
            </button>
            {!LOCAL_ONLY && (
              <>
                <button className="btn" onClick={() => go('create')}>
                  Criar sala <small>online com amigos</small>
                </button>
                <button className="btn" onClick={() => go('join')}>
                  Entrar numa sala <small>com um código</small>
                </button>
              </>
            )}
            <button className="btn ghost" onClick={() => go('about')}>
              Regras completas e créditos
            </button>
          </div>
          {(error || online.error) && (
            <div className="error-box" style={{ marginTop: '1rem', maxWidth: 360 }}>
              {error ?? online.error}
            </div>
          )}
        </div>
        <div className="hero-art" aria-hidden>
          <MapView view={demo} title="Atlas" />
        </div>
      </div>

      <ul className="facts" aria-label="O jogo em números">
        {FACTS.map((f) => (
          <li key={f.label}>
            <span className="n">{f.n}</span>
            <span className="l">{f.label}</span>
          </li>
        ))}
      </ul>

      <section className="home-section">
        <h2 className="home-title">Como se joga</h2>
        <ol className="steps">
          {STEPS.map((st, i) => (
            <li key={st.title}>
              <span className="step-n">{i + 1}</span>
              <Icon name={st.icon} size={26} />
              <h3>{st.title}</h3>
              <p>{st.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="home-section split">
        <div>
          <h2 className="home-title">Loot dos bosses</h2>
          <p className="home-text">
            Quem dá o último golpe num boss escolhe uma carta de loot; a primeira guilda a dominar cada região também ganha uma. São 15 cartas
            de uso único: runas de ataque, poções, amuletos, muralhas mágicas, contratos de mercenários e a Passagem do Capitão. Guarde até 3.
          </p>
        </div>
        <div className="loot-fan" aria-label="Algumas cartas de loot">
          {SAMPLE_LOOT.map((k, i) => (
            <div key={k} className="fan-slot" style={{ ['--i' as string]: i - (SAMPLE_LOOT.length - 1) / 2 }}>
              <GameCard desc={{ deck: 'loot', kind: k }} size="md" inspect={false} />
            </div>
          ))}
        </div>
      </section>

      <section className="home-section split reverse">
        <div className="card-spread" aria-label="As cinco vocações">
          {(['defense', 'range', 'damage', 'heal', 'harmony'] as const).map((v) => (
            <GameCard key={v} desc={{ deck: 'vocation', vocation: v }} size="md" inspect={false} />
          ))}
        </div>
        <div>
          <h2 className="home-title">Vocações</h2>
          <p className="home-text">
            Cada guilda escolhe uma vocação antes da partida, e o poder dela vale o jogo inteiro. Não existe a melhor: o Knight segura a
            fronteira, o Paladin alcança mais longe, o Sorcerer enfraquece o alvo antes do ataque, o Druid mantém o exército de pé e o
            Monk, a vocação mais nova do Tibia, acumula Harmonia para um turno decisivo.
          </p>
          <ul className="voc-notes">
            {Object.values(theme.vocations).map((v) => (
              <li key={v.id}>
                <strong>{v.name}.</strong> {v.flavor}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="home-section">
        <h2 className="home-title">Os bosses</h2>
        <p className="home-text">
          No começo de cada rodada, um 5 ou 6 no dado faz um boss surgir num covil. Se o timer zerar antes de alguém matá-lo, a raid castiga
          quem está em volta.
        </p>
        <div className="boss-grid">
          {Object.entries(theme.bosses).map(([id, b]) => {
            const def = theme.content.bosses.find((x) => x.id === id);
            return (
              <article key={id} className="boss-tile" style={{ ['--boss-color' as string]: b.color }}>
                <div className="sig">
                  <img src={sigilOf(id)} alt="" width={54} height={54} />
                </div>
                <div>
                  <h3>{b.name}</h3>
                  <div className="sc t">
                    {b.title}
                    {def ? ` · ${def.hp} de vida` : ''}
                  </div>
                  <p>{b.raidText}</p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="home-section">
        <h2 className="home-title">As guildas</h2>
        <p className="home-text">Seis guildas lendárias da história do Tibia. Cada jogador comanda uma; a cor da guilda é a cor das suas bandeiras no mapa.</p>
        <div className="guild-grid">
          {theme.guilds.map((g) => (
            <article key={g.id} className="guild-tile" style={{ ['--guild' as string]: g.hex }}>
              <img src={crestOf(g.color)} alt="" width={44} height={44} />
              <div>
                <h3>{g.name}</h3>
                <div className="sc t">{g.world ?? '—'}</div>
                <p>{g.flavor}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="home-section">
        <h2 className="home-title">Crônicas das guerras</h2>
        <p className="home-text">
          As guerras de guildas moldaram o Tibia, e cada uma virou uma era que você escolhe ao começar a partida. A história vem de relatos da
          comunidade; a regra muda a mesa.
        </p>
        <div className="chronicles">
          {CHRONICLES.map((c, i) => (
            <article key={c.id} className="chronicle">
              <div className="chron-top">
                <span className="chron-n">{['I', 'II', 'III', 'IV', 'V', 'VI'][i]}</span>
                <div>
                  <h3>{c.title}</h3>
                  <div className="sc chron-when">
                    {c.world} · {c.period}
                  </div>
                </div>
                <div className="chron-crests">
                  {c.guilds.map((g) => {
                    const gd = theme.guilds.find((x) => x.id === g);
                    return gd ? <img key={g} src={crestOf(gd.color)} alt={gd.name} title={gd.name} width={26} height={26} /> : null;
                  })}
                </div>
              </div>
              <p className="chron-story">{c.story}</p>
              <p className="chron-changed">
                <strong>No Tibia:</strong> {c.changed}
              </p>
              <p className="chron-rule">
                <strong>Na mesa:</strong> {c.rule}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="home-section">
        <h2 className="home-title">Seus rivais</h2>
        <p className="home-text">Os bots têm nome, cidade e história. Na partida, clique num jogador da lista para ver a ficha dele.</p>
        <div className="rivals">
          <div className="rival-list" role="tablist" aria-label="Rivais">
            {BOT_NAMES.map((n) => (
              <button key={n} role="tab" aria-selected={rival === n} className={rival === n ? 'on' : ''} onClick={() => setRival(n)}>
                <span className="rn">{n}</span>
                <span className="re">{CHARACTERS[n].epithet}</span>
              </button>
            ))}
          </div>
          <div className="hud-card rival-sheet" role="tabpanel" key={rival}>
            <CharacterSheet name={rival} color={rivalColor} bot />
          </div>
        </div>
      </section>
    </div>
  );
}
